/**
 * AssignPanel — ランディングの CTA に紐付けるフォームを多選択。
 *
 * 設計判断:
 *  - フォームのみ（v1.0）。コンバージョン後アクション（メール / LINE 配信）は
 *    キャンペーン側で設計するため、Landing から重複して設定しない。
 *  - 紐付けたフォームの最初の 1 つは CTA フォールバック URL として
 *    自動的に CTA ボタンのリンク先に使われる（utils/ctaFallback.ts）。
 *  - sourceCampaignId が設定されていれば ?campaignId=... も自動付与される
 *    → フォーム送信時にキャンペーンに紐付いた申込として記録される。
 */
import { Check, ExternalLink } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { Label, Textarea } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { toast } from '@/components/ui/toast';
import { useForms } from '@/hooks/useForms';
import { useUpdateLanding, type LandingRecord } from '@/hooks/useLandings';

interface Props {
  landingId: string;
  landing: LandingRecord;
}

export function AssignPanel({ landingId, landing }: Props) {
  const { data: forms } = useForms();
  const updateLanding = useUpdateLanding();

  const formIds = useMemo(
    () => landing.assignedCampaigns?.formIds ?? [],
    [landing.assignedCampaigns],
  );

  const toggle = async (id: string) => {
    const next = formIds.includes(id)
      ? formIds.filter((x) => x !== id)
      : [...formIds, id];
    try {
      await updateLanding.mutateAsync({
        id: landingId,
        data: {
          assignedCampaigns: {
            // 既存の mailRecipientIds / lineNewsIds は破壊しない（後方互換）
            mailRecipientIds: landing.assignedCampaigns?.mailRecipientIds ?? [],
            lineNewsIds: landing.assignedCampaigns?.lineNewsIds ?? [],
            formIds: next,
          },
        },
      });
    } catch (e) {
      toast.error(`更新失敗: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  const current = landing.googleTag;
  const isDisabled = current?.disabled ?? false;

  // 既存の googleTag をベースに部分更新（disabled / headHtml / bodyHtml を保持）
  const saveGoogleTag = async (patch: Partial<{
    headHtml: string;
    bodyHtml: string;
    disabled: boolean;
  }>) => {
    const next = {
      headHtml: current?.headHtml ?? '',
      bodyHtml: current?.bodyHtml ?? '',
      disabled: current?.disabled ?? false,
      ...patch,
    };
    try {
      await updateLanding.mutateAsync({
        id: landingId,
        data: { googleTag: next },
      });
    } catch (e) {
      toast.error(`更新失敗: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  const items = (forms ?? []).map((f) => ({
    id: f.id,
    name: f.name || '(無題)',
  }));

  return (
    <div className="space-y-3">
      <p className="text-xs text-[var(--text-muted)]">
        CTA ボタンのリンク先となる<strong>申込フォーム</strong>を紐付けます（複数選択可）。
        <br />
        紐付けると、各セクションの「詳細を見る」「申し込む」等のボタンが、
        最初に選択したフォームの公開 URL に自動でリンクします。
        {landing.sourceCampaignId && (
          <>
            <br />
            キャンペーンID も自動で付与されるため、フォーム送信は本キャンペーンへの申込として記録されます。
          </>
        )}
      </p>

      <div className="border border-[var(--border)] rounded-md p-2 bg-[var(--bg-elevated)]">
        <Label className="text-xs font-semibold">フォーム</Label>
        {items.length === 0 ? (
          <div className="text-[11px] text-[var(--text-muted)] py-2">
            まだフォームがありません。フォーム機能で作成してください。
          </div>
        ) : (
          <div className="space-y-1 mt-1 max-h-80 overflow-y-auto">
            {items.map((item) => {
              const checked = formIds.includes(item.id);
              return (
                <div
                  key={item.id}
                  className="flex items-center gap-2 px-2 py-1 rounded hover:bg-[var(--bg-hover)]"
                >
                  <button
                    type="button"
                    onClick={() => toggle(item.id)}
                    className={`size-4 rounded border flex items-center justify-center flex-shrink-0 ${
                      checked
                        ? 'bg-[var(--accent)] border-[var(--accent)]'
                        : 'border-[var(--border)]'
                    }`}
                  >
                    {checked && <Check className="size-3 text-white" />}
                  </button>
                  <span className="text-xs flex-1 truncate">{item.name}</span>
                  <a
                    href={`/public/forms/${item.id}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-[var(--text-muted)] hover:text-[var(--accent)]"
                    title="フォームを別タブで開く"
                  >
                    <ExternalLink className="size-3" />
                  </a>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="border border-[var(--border)] rounded-md p-2 bg-[var(--bg-elevated)] space-y-2">
        <Label className="text-xs font-semibold">Googleタグ（このLP個別）</Label>
        <p className="text-[10px] text-[var(--text-muted)] leading-relaxed">
          設定するとこのLPの既定タグ（設定＞Googleタグ）を<strong>上書き</strong>し、
          このLPのCTAから開いたフォームにも<strong>自動適用</strong>されます。
          空欄なら既定タグが使われます。
        </p>

        {/* オプトアウト: このLPには一切タグを注入しない */}
        <div className="rounded border border-[var(--border)] bg-[var(--bg-surface)] px-3">
          <Switch
            label="このLPには計測タグを挿入しない"
            description="ONにすると、既定タグが有効でもこのLPだけ除外します。下のA/Bタグも無視され、このLPのCTAから開いたフォームにもタグは入りません。"
            checked={isDisabled}
            onChange={(e) => void saveGoogleTag({ disabled: e.target.checked })}
          />
        </div>

        {isDisabled ? (
          <p className="text-[10px] text-[var(--text-muted)] italic">
            現在このLPはタグ挿入の対象外です（除外がONのため）。
          </p>
        ) : (
          <>
            <GoogleTagField
              label="Aタグ（<head> 用）"
              value={landing.googleTag?.headHtml ?? ''}
              onSave={(v) => saveGoogleTag({ headHtml: v })}
            />
            <GoogleTagField
              label="Bタグ（<body> 直下用）"
              value={landing.googleTag?.bodyHtml ?? ''}
              onSave={(v) => saveGoogleTag({ bodyHtml: v })}
            />
          </>
        )}
      </div>

      <p className="text-[10px] text-[var(--text-muted)] pt-2 border-t border-[var(--border)]">
        💡 コンバージョン後のアクション（メール / LINE 配信等）は<strong>キャンペーン</strong>機能で設計してください。
      </p>
    </div>
  );
}

/** IME 安全に blur 保存する Google タグ用 textarea。 */
function GoogleTagField({
  label,
  value,
  onSave,
}: {
  label: string;
  value: string;
  onSave: (v: string) => Promise<unknown> | void;
}) {
  const [local, setLocal] = useState(value);
  useEffect(() => setLocal(value), [value]);
  const composing = useRef(false);

  const handleBlur = () => {
    if (composing.current) return;
    const trimmed = local.trim();
    if (trimmed === value.trim()) return;
    void onSave(trimmed);
  };

  return (
    <div>
      <Label className="text-[11px] text-[var(--text-muted)]">{label}</Label>
      <Textarea
        rows={3}
        value={local}
        onChange={(e) => setLocal(e.target.value)}
        onBlur={handleBlur}
        onCompositionStart={() => (composing.current = true)}
        onCompositionEnd={() => (composing.current = false)}
        spellCheck={false}
        className="font-mono text-[11px]"
        placeholder="（空欄＝既定タグを使用）"
      />
    </div>
  );
}
